
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { getClassifications } from '../contexts/constants';
import { useLanguage } from '../contexts/LanguageContext';
import { usePatient } from '../contexts/PatientContext';
import { 
  ShieldCheck, 
  AlertCircle, 
  Clock, 
  Pill, 
  FilePlus, 
  Check, 
  BookOpen, 
  PackageCheck, 
  XCircle, 
  CheckCircle2, 
  Syringe, 
  Timer, 
  Play, 
  RotateCcw, 
  Pause, 
  Box, 
  ClipboardList,
  Search,
  Sparkles,
  BookMarked
} from 'lucide-react';
import { ClinicalNote, InventoryItem, createInitialClinicalRegistry } from "../contexts/types";
import { searchOrthopedicGuidelines } from '../services/geminiService';

// Helper for Markdown Rendering
const parseFormatting = (text: string) => {
  // Split by bold (**text**) and italic (*text*) markers
  const parts = text.split(/(\*\*.*?\*\*|\*.*?\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index} className="text-white font-bold">{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      return <span key={index} className="text-slate-200 italic">{part.slice(1, -1)}</span>;
    }
    return part;
  });
};

const renderMarkdown = (text: string) => {
  if (!text) return null;
  
  // Normalize line endings
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  
  return lines.map((line, i) => {
    const cleanLine = line.trim();
    
    // Headers (H1, H2, H3...) - Replace # with styled headings
    if (cleanLine.startsWith('#')) {
        const level = cleanLine.match(/^#+/)?.[0].length || 0;
        const content = cleanLine.replace(/^#+\s*/, '');
        
        if (level === 1) return <h3 key={i} className="text-xl font-bold text-white mt-6 mb-3 border-b border-slate-700/50 pb-2">{parseFormatting(content)}</h3>;
        if (level === 2) return <h4 key={i} className="text-lg font-bold text-blue-400 mt-5 mb-2">{parseFormatting(content)}</h4>;
        return <h5 key={i} className="text-md font-bold text-emerald-400 mt-4 mb-1 uppercase tracking-wide">{parseFormatting(content)}</h5>;
    }
    
    // Bullet Lists (*, -, •)
    if (cleanLine.startsWith('* ') || cleanLine.startsWith('- ') || cleanLine.startsWith('• ')) {
        const content = cleanLine.replace(/^[*•-]\s*/, '');
        return (
            <div key={i} className="flex gap-3 mb-2 ml-1 items-start group">
                <span className="text-blue-500 mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0 block shadow-[0_0_8px_rgba(59,130,246,0.5)] group-hover:bg-blue-400 transition-colors"></span>
                <span className="text-slate-300 text-sm leading-relaxed">{parseFormatting(content)}</span>
            </div>
        );
    }

    // Numbered Lists
    if (/^\d+\.\s/.test(cleanLine)) {
        const number = cleanLine.match(/^\d+\./)?.[0];
        const content = cleanLine.replace(/^\d+\.\s*/, '');
        return (
            <div key={i} className="flex gap-3 mb-2 ml-1 items-start">
                <span className="text-blue-400 font-bold text-sm min-w-[20px]">{number}</span>
                <span className="text-slate-300 text-sm leading-relaxed">{parseFormatting(content)}</span>
            </div>
        );
    }

    // Empty lines
    if (!cleanLine) return <div key={i} className="h-3" />;

    // Standard Paragraphs
    return <p key={i} className="text-slate-300 text-sm leading-relaxed mb-2">{parseFormatting(cleanLine)}</p>;
  });
};

const DecisionModule: React.FC = () => {
  const { language, t } = useLanguage();
  const { activePatient, updatePatient } = usePatient();
  const [saved, setSaved] = useState(false);

  const checklistTranslationKeyMap: Record<string, string> = {
    'Planejamento 3D': 'decisionChecklistPlanning3d',
    'Avaliar Neurovascular': 'decisionChecklistAssessNeurovascular',
    'Tração em Mesa': 'decisionChecklistTableTraction',
    'Doppler/Angio': 'decisionChecklistDopplerAngio',
    'Shunt Temporário': 'decisionChecklistTemporaryShunt',
    'Estabilização Óssea': 'decisionChecklistBoneStabilization',
    'Cobertura VAC': 'decisionChecklistVacCoverage',
    'Desbridamento Radical': 'decisionChecklistRadicalDebridement',
    'Second Look 48h': 'decisionChecklistSecondLook48h',
    'Planejar Retalho': 'decisionChecklistPlanFlap',
    'Desbridamento': 'decisionChecklistDebridement',
    'Lavagem 6L': 'decisionChecklistIrrigation6L',
    'Fechamento primário tardio': 'decisionChecklistDelayedPrimaryClosure',
    'Ampliação da ferida': 'decisionChecklistWoundExtension',
    'Lavagem mecânica': 'decisionChecklistMechanicalIrrigation',
    'Limpeza local': 'decisionChecklistLocalCleaning',
    'Sutura primária': 'decisionChecklistPrimaryClosure',
    'Tétano': 'decisionChecklistTetanus',
    'Jejum': 'decisionChecklistFasting',
    'Reserva de Sangue': 'decisionChecklistBloodReservation',
    'Risco Cardiológico': 'decisionChecklistCardiacRisk',
    'Avaliar estoque ósseo': 'decisionChecklistAssessBoneStock',
    'Posicionamento Mesa': 'decisionChecklistTablePositioning',
    'Intensificador': 'decisionChecklistCArm',
    'Carga parcial protegida': 'decisionChecklistProtectedPartialWeightBearing',
    'Sinais Compartimentais': 'decisionChecklistCompartmentSigns',
    'TC 3D': 'decisionChecklistCt3d',
    'Planejar Vias': 'decisionChecklistPlanApproaches',
    'TC Obrigatória': 'decisionChecklistCtMandatory',
    'Monitorar pele': 'decisionChecklistMonitorSkin',
    'Avaliar nervo fibular': 'decisionChecklistAssessPeronealNerve',
    'Artéria poplítea': 'decisionChecklistPoplitealArtery',
    'Janela cortical': 'decisionChecklistCorticalWindow',
    'Fluoroscopia': 'decisionChecklistFluoroscopy',
    'Menisco lateral (reparo?)': 'decisionChecklistLateralMeniscusRepair',
    'Teste de Cotton': 'decisionChecklistCottonTest',
    'Explorar Deltoide?': 'decisionChecklistExploreDeltoid',
    'Estabilidade medial': 'decisionChecklistMedialStability',
    'Rx Stress': 'decisionChecklistStressXray',
    'Carga conforme dor': 'decisionChecklistWeightBearingAsTolerated',
    'Alertar pais sobre prognóstico': 'decisionChecklistWarnParentsPrognosis',
    'RNM se dúvida': 'decisionChecklistMriIfUncertain',
    'Não cruzar fise com rosca': 'decisionChecklistDoNotCrossPhysis',
    'Redução perfeita': 'decisionChecklistPerfectReduction',
    'Restaurar superfície articular': 'decisionChecklistRestoreArticularSurface',
    'Manobra suave': 'decisionChecklistGentleManeuver',
    'Rx Controle 1 semana': 'decisionChecklistControlXray1Week',
    'Pendular precoce': 'decisionChecklistEarlyPendulum',
    'Suturas Manguito': 'decisionChecklistCuffSutures',
    'Enxerto Ósseo': 'decisionChecklistBoneGraft',
    'Amarra dos tubérculos': 'decisionChecklistTuberosityFixation',
    'Avaliar nervo axilar': 'decisionChecklistAssessAxillaryNerve',
    'Avaliar pele': 'decisionChecklistAssessSkin',
    'RX Axilar': 'decisionChecklistAxillaryXray',
    'Neurovascular': 'decisionChecklistNeurovascular',
    'TC Ombro': 'decisionChecklistShoulderCt',
    'Avaliar pele (risco necrose)': 'decisionChecklistAssessSkinNecrosisRisk',
    'Discutir demanda funcional': 'decisionChecklistDiscussFunctionalDemand',
    'Rx Comparativo': 'decisionChecklistComparativeXray',
    'Analgesia': 'decisionChecklistAnalgesia',
    'Reavaliação 2 sem': 'decisionChecklistReevaluation2Weeks',
    'Retorno conforme dor': 'decisionChecklistReturnAsTolerated',
    'Túnel do Carpo': 'decisionChecklistCarpalTunnel',
    'Partes Moles': 'decisionChecklistSoftTissues',
    'Redução Articular Perfeita': 'decisionChecklistPerfectArticularReduction',
    'Degrau articular < 1mm': 'decisionChecklistArticularStepLt1mm',
    'Índices radiológicos': 'decisionChecklistRadiographicIndices',
    'Embolização pré-op?': 'decisionChecklistPreOpEmbolization',
    'Tração esquelética': 'decisionChecklistSkeletalTraction',
    'Nervo Ciático': 'decisionChecklistSciaticNerve',
    'Redução da luxação': 'decisionChecklistReduceDislocation',
    'Planejamento 3D Obrigatório': 'decisionChecklistMandatory3dPlanning',
    'Nervo Femoral Cutâneo Lateral': 'decisionChecklistLateralFemoralCutaneousNerve',
  };

  const inventoryTranslationKeyMap: Record<string, string> = {
    'Placa Bloqueada LCP': 'decisionInventoryMaterialLockedLcpPlate',
    'Haste Intramedular': 'decisionInventoryMaterialIntramedullaryNail',
    'Parafusos 3.5/4.5mm': 'decisionInventoryMaterialScrews3545mm',
    'Kit Fixador Externo': 'decisionInventoryMaterialExternalFixatorKit',
    'Kit Vascular': 'decisionInventoryMaterialVascularKit',
    'Soro 9L': 'decisionInventoryMaterialSaline9L',
    'Curativo VAC': 'decisionInventoryMaterialVacDressing',
    'Soro 6L': 'decisionInventoryMaterialSaline6L',
    'Haste/Placa': 'decisionInventoryMaterialNailPlate',
    'Soro 3L': 'decisionInventoryMaterialSaline3L',
    'Prótese de Quadril': 'decisionInventoryMaterialHipProsthesis',
    'Parafusos Canulados 7.3mm': 'decisionInventoryMaterialCannulatedScrews73mm',
    'DHS / Canulados': 'decisionInventoryMaterialDhsCannulated',
    'Parafusos Canulados 7.3mm (x3)': 'decisionInventoryMaterialCannulatedScrews73mmX3',
    'Fixador Externo': 'decisionInventoryMaterialExternalFixator',
    'Placas Bloqueadas 3.5/4.5mm': 'decisionInventoryMaterialLockedPlates3545mm',
    'Placa Bloqueada Lateral': 'decisionInventoryMaterialLateralLockingPlate',
    'Placa de Suporte Medial': 'decisionInventoryMaterialMedialSupportPlate',
    'Placa T ou L 4.5mm': 'decisionInventoryMaterialPlateTL45mm',
    'Enxerto Ósseo': 'decisionInventoryMaterialBoneGraft',
    'Parafusos 6.5mm': 'decisionInventoryMaterialScrews65mm',
    'Placa Bloqueada Tíbia Proximal': 'decisionInventoryMaterialProximalTibiaLockingPlate',
    'Placa 1/3 Tubo': 'decisionInventoryMaterialOneThirdTubePlate',
    'Parafuso Sindesmose 3.5/4.5mm': 'decisionInventoryMaterialSyndesmoticScrew3545mm',
    'Parafusos Lag 3.5mm': 'decisionInventoryMaterialLagScrews35mm',
    'Bota Imobilizadora': 'decisionInventoryMaterialWalkingBoot',
    'Gesso': 'decisionInventoryMaterialCast',
    'Parafusos Canulados Pequenos': 'decisionInventoryMaterialSmallCannulatedScrews',
    'Fios K': 'decisionInventoryMaterialKWires',
    'Parafusos ou Fios': 'decisionInventoryMaterialScrewsOrWires',
    'Gesso, Fios K (opcional)': 'decisionInventoryMaterialCastKWireOptional',
    'Tipoia Velpeau': 'decisionInventoryMaterialVelpeauSling',
    'Placa Bloqueada (Philos)': 'decisionInventoryMaterialPhilosPlate',
    'Prótese Reversa': 'decisionInventoryMaterialReverseProsthesis',
    'Placa Bloqueada Proximal': 'decisionInventoryMaterialProximalLockingPlate',
    'Suturas Alta Resistência': 'decisionInventoryMaterialHighStrengthSutures',
    'Haste Umeral': 'decisionInventoryMaterialHumeralNail',
    'Placa Bloqueada': 'decisionInventoryMaterialLockingPlate',
    'Âncora/Button': 'decisionInventoryMaterialAnchorButton',
    'Fios Kirschner': 'decisionInventoryMaterialKirschnerWires',
    'Material de Revisão': 'decisionInventoryMaterialRevisionMaterial',
    'Tightrope/Endobutton': 'decisionInventoryMaterialTightropeEndobutton',
    'Enxerto (se crônico)': 'decisionInventoryMaterialGraftIfChronic',
    'Tipoia': 'decisionInventoryMaterialSling',
    'Tipoia Simples': 'decisionInventoryMaterialSimpleSling',
    'Gelo': 'decisionInventoryMaterialIce',
    'Fixador Externo, Placa Volar, Fios K': 'decisionInventoryMaterialExternalFixatorVolarPlateKWires',
    'Placa Volar Bloqueada': 'decisionInventoryMaterialVolarLockingPlate',
    'Enxerto Ósseo, Parafusos/Placa': 'decisionInventoryMaterialBoneGraftScrewsPlate',
    'Gesso, Placa Volar (opcional)': 'decisionInventoryMaterialCastVolarPlateOptional',
    'Placas de Reconstrução 3.5mm (x2)': 'decisionInventoryMaterialReconstructionPlates35mmX2',
    'Parafusos Longos': 'decisionInventoryMaterialLongScrews',
    'Placa de Reconstrução': 'decisionInventoryMaterialReconstructionPlate',
    'Parafusos corticais': 'decisionInventoryMaterialCorticalScrews',
    'Placas Curvas': 'decisionInventoryMaterialCurvedPlates',
  };

  const translateChecklistItem = (item: string) => t(checklistTranslationKeyMap[item] || item);
  const translateInventoryItem = (item: string) => t(inventoryTranslationKeyMap[item] || item);
  
  // Classifications
  const classifications = useMemo(() => getClassifications(language), [language]);
  const [selectedId, setSelectedId] = useState<string>(classifications[0]?.id);
  const classification = useMemo(() => 
    classifications.find(c => c.id === selectedId) || classifications[0],
    [classifications, selectedId]
  );

  // Initialize with values to prevent render crash before effect runs
  const [selections, setSelections] = useState<string[]>(() => 
    classification ? classification.criteria.map(c => c.options[0]) : []
  );
  
  // Antibiotic Calculator State
  const [patientWeight, setPatientWeight] = useState<string>(activePatient?.weight || '');
  const [antibioticTimer, setAntibioticTimer] = useState<number>(0); // in seconds
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [nextDoseTime, setNextDoseTime] = useState<Date | null>(null);
  const timerIntervalRef = useRef<number | null>(null);

  // Inventory State
  const [inventoryStatus, setInventoryStatus] = useState<Record<string, string>>({});

  // Checklist State
  const [checklistState, setChecklistState] = useState<Record<string, boolean>>({});

  // Guidelines State
  const [guidelineQuery, setGuidelineQuery] = useState('');
  const [guidelineResult, setGuidelineResult] = useState('');
  const [loadingGuidelines, setLoadingGuidelines] = useState(false);

  useEffect(() => {
    if (classification) {
       setSelections(classification.criteria.map(c => c.options[0]));
       setSaved(false);
       setChecklistState({});
    }
  }, [classification]); // Re-run when classification changes

  // Determine readiness and calculate result safely
  const isReady = classification && selections.length === classification.criteria.length;
  const result = useMemo(() => {
    if (isReady) {
      return classification.getResult(selections);
    }
    return null;
  }, [isReady, classification, selections]);

  // --- EFFECT: Inventory Check ---
  useEffect(() => {
    if (result && result.materials) {
      const storedInv = localStorage.getItem('orthoprecision_inventory_items');
      const items: InventoryItem[] = storedInv ? JSON.parse(storedInv) : [];
      const statusMap: Record<string, string> = {};

      result.materials.forEach(matName => {
        // Simple fuzzy match
        const found = items.find(i => 
          i.name.toLowerCase().includes(matName.toLowerCase()) || 
          matName.toLowerCase().includes(i.name.toLowerCase())
        );
        statusMap[matName] = found ? found.availability : 'Unknown';
      });
      setInventoryStatus(statusMap);
    }
  }, [result]);

  // --- EFFECT: Antibiotic Timer ---
  useEffect(() => {
    if (isTimerRunning && nextDoseTime) {
      timerIntervalRef.current = window.setInterval(() => {
        const now = new Date();
        const diff = Math.ceil((nextDoseTime.getTime() - now.getTime()) / 1000);
        if (diff <= 0) {
          setAntibioticTimer(0);
          setIsTimerRunning(false);
          // Play sound or alert here
        } else {
          setAntibioticTimer(diff);
        }
      }, 1000);
    } else {
      if (timerIntervalRef.current) window.clearInterval(timerIntervalRef.current);
    }
    return () => { if (timerIntervalRef.current) window.clearInterval(timerIntervalRef.current); };
  }, [isTimerRunning, nextDoseTime]);

  const handleOptionChange = (criteriaIndex: number, option: string) => {
    const newSelections = [...selections];
    newSelections[criteriaIndex] = option;
    setSelections(newSelections);
  };

  const handleStartTimer = () => {
    if (result && result.antibioticDetails) {
      const hours = result.antibioticDetails.frequencyHours;
      const target = new Date(new Date().getTime() + hours * 60 * 60 * 1000);
      setNextDoseTime(target);
      setIsTimerRunning(true);
    }
  };

  const handleResetTimer = () => {
    setIsTimerRunning(false);
    setAntibioticTimer(0);
    setNextDoseTime(null);
  };

  const formatTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h}h ${m}m ${s}s`;
  };

  const toggleChecklist = (item: string) => {
    setChecklistState(prev => ({ ...prev, [item]: !prev[item] }));
  };

  const handleSaveToPatient = () => {
    if (!activePatient) {
      alert(t('selectPatientPatientsTabFirst'));
      return;
    }

    if (!result) return;

    const calculatedDose = result.antibioticDetails && patientWeight 
        ? `${Math.min(parseFloat(patientWeight) * result.antibioticDetails.doseMgKg, result.antibioticDetails.maxDoseMg || 9999).toFixed(0)}mg` 
        : 'N/A';

    const content = `${t('decisionSavedClassification', { value: classification.name })}
${t('decisionSavedResult', { title: result.title, urgency: result.urgency })}
${t('decisionSavedDescription', { value: result.description })}
${t('decisionSavedConduct', { value: result.action })}
${t('decisionSavedAntibiotic', { value: `${result.antibiotics} (${calculatedDose})` })}
${t('decisionSavedSafetyChecklist')}
${result.checklist?.map(c => `[${checklistState[c] ? 'x' : ' '}] ${translateChecklistItem(c)}`).join('\n') || 'N/A'}
${t('decisionSavedExpectedMaterials', { value: result.materials?.map(translateInventoryItem).join(', ') || 'N/A' })}`;

    const newNote: ClinicalNote = {
      id: Date.now().toString(),
      date: Date.now(),
      type: 'Protocol',
      title: t('decisionSavedProtocolTitle', { name: classification.name }),
      content: content
    };

    const existingRegistry = activePatient.clinicalRegistry ?? createInitialClinicalRegistry();
    const inferredClassificationSystem = classification.name.split(' (')[0] || '';
    const inferredBone =
      activePatient.anatomy ||
      classification.name.match(/\(([^)]+)\)/)?.[1] ||
      existingRegistry.diagnosis.bone ||
      '';

    updatePatient(activePatient.id, {
      clinicalNotes: [newNote, ...(activePatient.clinicalNotes || [])],
      clinicalRegistry: {
        ...existingRegistry,
        diagnosis: {
          ...existingRegistry.diagnosis,
          classificationSystem: inferredClassificationSystem || existingRegistry.diagnosis.classificationSystem,
          classificationCode: result.title || existingRegistry.diagnosis.classificationCode,
          bone: inferredBone,
        },
        treatment: {
          ...existingRegistry.treatment,
          strategy: result.action || existingRegistry.treatment.strategy,
        },
      },
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleGuidelineSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!guidelineQuery.trim()) return;
    setLoadingGuidelines(true);
    try {
        const text = await searchOrthopedicGuidelines(guidelineQuery, language);
        setGuidelineResult(text);
    } catch (e) {
        console.error(e);
        setGuidelineResult(t("guidelineSearchError"));
    } finally {
        setLoadingGuidelines(false);
    }
  };

  const urgencyColors: Record<string, string> = {
    'Low': 'bg-green-500/20 text-green-400 border-green-500/30',
    'Medium': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    'High': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    'Immediate': 'bg-red-500/20 text-red-400 border-red-500/30'
  };

  // Guard Clause now at the end of hooks
  if (!isReady || !result) {
    return <div className="p-10 text-center text-slate-500 animate-pulse">{t("loadProtocolParams")}</div>;
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-12">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">{t('decisionTitle')}</h2>
          <p className="text-slate-400">{t('decisionSubtitle')}</p>
        </div>
        <select 
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
          className="bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white outline-none focus:ring-2 focus:ring-blue-500 transition-all cursor-pointer hover:bg-slate-700"
        >
          {classifications.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Left Column: Input & References */}
        <div className="space-y-6">
            <div className="glass p-6 rounded-2xl space-y-6 shadow-xl border border-slate-700/50">
                <h3 className="text-lg font-semibold flex items-center gap-2 text-blue-100">
                    <ShieldCheck className="w-5 h-5 text-blue-400" />
                    {t('caseParams')}
                </h3>
                <div className="space-y-6">
                    {classification.criteria.map((criterion, idx) => (
                    <div key={idx} className="space-y-3">
                        <label className="text-sm text-slate-400 font-medium uppercase tracking-wider text-[10px]">{criterion.label}</label>
                        <div className="flex flex-wrap gap-2">
                        {criterion.options.map(option => (
                            <button
                            key={option}
                            onClick={() => handleOptionChange(idx, option)}
                            className={`px-4 py-2 rounded-lg text-sm transition-all border font-medium ${
                                selections[idx] === option 
                                ? 'bg-blue-600 border-blue-500 text-white shadow-lg shadow-blue-500/20' 
                                : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-500 hover:text-slate-200'
                            }`}
                            >
                            {option}
                            </button>
                        ))}
                        </div>
                    </div>
                    ))}
                </div>
            </div>

            {/* Scientific References */}
            {result.references && result.references.length > 0 && (
                <div className="glass p-4 rounded-xl border border-slate-700/50 bg-slate-800/30">
                    <h4 className="text-xs font-bold text-slate-500 uppercase flex items-center gap-2 mb-3">
                        <BookOpen className="w-3 h-3" /> {t('decisionScientificBasis')}
                    </h4>
                    <ul className="space-y-2">
                        {result.references.map((ref, i) => (
                            <li key={i} className="text-[10px] text-slate-400 border-l-2 border-slate-600 pl-2 italic">
                                {ref}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>

        {/* Right Column: Results & Tools */}
        <div className="space-y-6">
            
            {/* Main Result Card */}
            <div className="glass p-6 rounded-2xl border-l-4 border-l-blue-500 space-y-6 shadow-xl bg-slate-800/20 relative">
                <div className="flex items-start justify-between">
                    <div className="space-y-2">
                        <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest border ${urgencyColors[result.urgency] || urgencyColors['Medium']}`}>
                            {result.urgency === 'Immediate' ? t("emergencyLabel") : result.urgency}
                        </span>
                        <h3 className="text-3xl font-bold text-white tracking-tight">{result.title}</h3>
                    </div>
                    <div className="p-4 bg-blue-500/10 rounded-2xl border border-blue-500/20">
                        <AlertCircle className="w-8 h-8 text-blue-400" />
                    </div>
                </div>

                <div className="space-y-4">
                    <div className="p-4 bg-slate-800/80 rounded-xl border border-slate-700">
                        <p className="text-slate-200 text-sm leading-relaxed">{result.description}</p>
                    </div>

                    <div className="space-y-3">
                        <h4 className="text-xs font-bold text-slate-500 flex items-center gap-2 uppercase tracking-widest">
                            <Clock className="w-3 h-3" /> {t('recommendedAction')}
                        </h4>
                        <div className="p-4 bg-blue-600/10 text-blue-200 rounded-xl border border-blue-500/20 font-medium text-sm">
                            {result.action}
                        </div>
                    </div>
                </div>
            </div>

            {/* Antibiotic Calculator */}
            {result.antibioticDetails && (
                <div className="glass p-6 rounded-2xl border border-slate-700 bg-emerald-900/10">
                    <h3 className="text-sm font-bold text-emerald-400 uppercase tracking-widest flex items-center gap-2 mb-4">
                        <Syringe className="w-4 h-4" /> {t('decisionAntibioticCalculator')}
                    </h3>
                    
                    <div className="flex gap-4 items-end mb-4">
                        <div className="flex-1">
                            <label className="text-[10px] text-slate-500 uppercase font-bold">{t('decisionWeightKg')}</label>
                            <input 
                                type="number" 
                                value={patientWeight}
                                onChange={(e) => setPatientWeight(e.target.value)}
                                className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white font-bold outline-none focus:border-emerald-500"
                                placeholder="00.0"
                            />
                        </div>
                        <div className="flex-1 bg-slate-800/50 p-2 rounded-lg border border-slate-700">
                            <div className="text-[10px] text-slate-500 uppercase">{t('decisionCalculatedDose')}</div>
                            <div className="text-xl font-bold text-white">
                                {patientWeight 
                                    ? `${Math.min(parseFloat(patientWeight) * result.antibioticDetails.doseMgKg, result.antibioticDetails.maxDoseMg || 9999).toFixed(0)}mg`
                                    : '--'
                                }
                            </div>
                        </div>
                        <div className="flex-1 bg-slate-800/50 p-2 rounded-lg border border-slate-700">
                            <div className="text-[10px] text-slate-500 uppercase">{t('decisionInterval')}</div>
                            <div className="text-lg font-bold text-slate-300">{result.antibioticDetails.frequencyHours}h</div>
                        </div>
                    </div>

                    <div className="flex items-center justify-between bg-slate-900/50 p-3 rounded-xl border border-slate-800">
                        <div className="flex items-center gap-2">
                            <Timer className={`w-5 h-5 ${isTimerRunning ? 'text-emerald-400 animate-pulse' : 'text-slate-500'}`} />
                            <div className="text-xs font-mono">
                                {isTimerRunning ? (
                                    <span className="text-emerald-400 font-bold">{formatTime(antibioticTimer)}</span>
                                ) : (
                                    <span className="text-slate-500">{t('decisionNextDose')}</span>
                                )}
                            </div>
                        </div>
                        <div className="flex gap-2">
                            {!isTimerRunning ? (
                                <button onClick={handleStartTimer} className="p-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-white transition-colors">
                                    <Play className="w-3 h-3" />
                                </button>
                            ) : (
                                <button onClick={() => setIsTimerRunning(false)} className="p-2 bg-amber-600 hover:bg-amber-500 rounded-lg text-white transition-colors">
                                    <Pause className="w-3 h-3" />
                                </button>
                            )}
                            <button onClick={handleResetTimer} className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-300 transition-colors">
                                <RotateCcw className="w-3 h-3" />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Inventory & Checklist Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Inventory Check */}
                {result.materials && result.materials.length > 0 && (
                    <div className="glass p-4 rounded-xl border border-slate-700 bg-slate-800/30">
                        <h4 className="text-xs font-bold text-slate-500 uppercase flex items-center gap-2 mb-3">
                            <Box className="w-3 h-3" /> {t('decisionInventory')}
                        </h4>
                        <div className="space-y-2">
                            {result.materials.map((mat, i) => {
                                const status = inventoryStatus[mat] || 'Unknown';
                                return (
                                    <div key={i} className="flex items-center justify-between text-xs bg-slate-900/50 p-2 rounded-lg border border-slate-800">
                                        <span className="text-slate-300 truncate pr-2">{translateInventoryItem(mat)}</span>
                                        {status === 'Available' ? (
                                            <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                                        ) : status === 'Low' ? (
                                            <AlertCircle className="w-4 h-4 text-amber-500 shrink-0" />
                                        ) : status === 'Out of Stock' ? (
                                            <XCircle className="w-4 h-4 text-red-500 shrink-0" />
                                        ) : (
                                            <span className="w-2 h-2 bg-slate-600 rounded-full"></span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Safety Checklist */}
                {result.checklist && result.checklist.length > 0 && (
                    <div className="glass p-4 rounded-xl border border-slate-700 bg-slate-800/30">
                        <h4 className="text-xs font-bold text-slate-500 uppercase flex items-center gap-2 mb-3">
                            <ClipboardList className="w-3 h-3" /> {t('decisionChecklist')}
                        </h4>
                        <div className="space-y-2">
                            {result.checklist.map((item, i) => (
                                <button 
                                    key={i} 
                                    onClick={() => toggleChecklist(item)}
                                    className={`w-full text-left flex items-start gap-2 p-2 rounded-lg text-xs transition-all border ${
                                        checklistState[item] 
                                        ? 'bg-blue-900/30 border-blue-500/50 text-blue-200' 
                                        : 'bg-slate-900/30 border-transparent hover:bg-slate-900/60 text-slate-400'
                                    }`}
                                >
                                    <div className={`w-3 h-3 rounded-full border mt-0.5 shrink-0 flex items-center justify-center ${checklistState[item] ? 'bg-blue-500 border-blue-500' : 'border-slate-600'}`}>
                                        {checklistState[item] && <Check className="w-2 h-2 text-white" />}
                                    </div>
                                    <span className={checklistState[item] ? 'line-through opacity-70' : ''}>{translateChecklistItem(item)}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Save Button */}
            {activePatient && (
                <div className="pt-4 border-t border-slate-700/50 flex justify-end">
                    <button 
                        onClick={handleSaveToPatient}
                        disabled={saved}
                        className={`w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl text-sm font-bold uppercase tracking-wide transition-all shadow-xl ${
                        saved 
                            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50' 
                            : 'bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white'
                        }`}
                    >
                        {saved ? <Check className="w-5 h-5" /> : <FilePlus className="w-5 h-5" />}
                        {saved ? t('decisionProtocolSaved') : t('saveToRecord')}
                    </button>
                </div>
            )}
        </div>
      </div>

      {/* NEW: GUIDELINES & UPDATES SECTION */}
      <div className="mt-8 pt-8 border-t border-slate-700">
         <div className="glass rounded-2xl border border-purple-500/20 shadow-xl overflow-hidden">
            <div className="p-6 border-b border-slate-700/50 bg-slate-800/30 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
               <div>
                  <h3 className="text-lg font-bold text-white flex items-center gap-2">
                     <BookMarked className="w-5 h-5 text-purple-400" /> {t("guidelinesTitle")}
                  </h3>
                  <p className="text-xs text-slate-400">{t("guidelinesSubtitle")}</p>
               </div>
               
               <form onSubmit={handleGuidelineSearch} className="flex gap-2 w-full md:w-auto">
                  <div className="relative flex-1 md:w-80">
                     <input 
                       type="text" 
                       value={guidelineQuery}
                       onChange={(e) => setGuidelineQuery(e.target.value)}
                      placeholder={t("guidelineSearchPlaceholder")}
                       className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-2 text-sm text-white focus:border-purple-500 outline-none"
                     />
                     <Search className="absolute right-3 top-2.5 w-4 h-4 text-slate-500" />
                  </div>
                  <button 
                    type="submit" 
                    disabled={loadingGuidelines || !guidelineQuery}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-xl text-xs font-bold uppercase tracking-wide flex items-center gap-2 disabled:opacity-50 transition-all"
                  >
                    {loadingGuidelines ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {t("search")}
                  </button>
               </form>
            </div>
            
            <div className="p-6 bg-slate-900/40 min-h-[100px]">
               {loadingGuidelines ? (
                  <div className="flex flex-col items-center justify-center py-8 text-slate-500 animate-pulse">
                     <Sparkles className="w-8 h-8 text-purple-500/50 mb-2" />
                     <p className="text-xs font-bold uppercase tracking-widest">{t('decisionSearchingMedicalDatabases')}</p>
                  </div>
               ) : guidelineResult ? (
                  <div className="prose prose-invert prose-sm max-w-none text-slate-300 leading-relaxed">
                     {renderMarkdown(guidelineResult)}
                  </div>
               ) : (
                  <div className="flex flex-col items-center justify-center py-8 text-slate-600 opacity-50">
                     <BookOpen className="w-12 h-12 mb-2" />
                     <p className="text-xs font-bold uppercase tracking-widest">{t('decisionGuidelinesEmptyState')}</p>
                  </div>
               )}
            </div>
         </div>
      </div>
    </div>
  );
};

export default DecisionModule;
